import { Box, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Icon from '@ui/Icon';

export interface ModalBodyPaperProps {
  includeDismissIcon: boolean;
  /**
   * When a boolean, it defaults to 344. If it's a number, it'll use that value.
   * */
  includeMaxWidth?: boolean | number;
  includeHeight?: string;
  includePadding?: boolean;
  backgroundColor?: 'paper' | 'paperSecondary';
  className?: string;
  onClose: () => void;
  classes?: {
    paperChild: string;
  };
  children?: React.ReactNode;
}

const useStyles = makeStyles<{
  includeHeight?: string;
  backgroundColor?: 'paper' | 'paperSecondary';
  includePadding?: boolean;
}>()((theme, { includeHeight, backgroundColor, includePadding }) => ({
  paper: {
    backgroundColor:
      backgroundColor === 'paper'
        ? theme.palette.background.paper
        : theme.palette.background.paperSecondary,
  },
  paperChild: {
    overflowY: includeHeight ? 'scroll' : 'initial',
    padding: includePadding ? theme.spacing(5, 4) : 0,
  },
  modalCloseButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: palette.gray_75,
    '&:hover': {
      backgroundColor: palette.gray_700,
    },
  },
}));

/**
 * ModalBodyPaper component provides a surface inside of a Modal with standardized background color
 * and padding.
 */
const ModalBodyPaper = (props: ModalBodyPaperProps) => {
  const {
    className,
    includeDismissIcon,
    includeMaxWidth,
    backgroundColor = 'paper',
    includeHeight,
    includePadding = true,
    onClose,
    children,
    classes: paperClasses,
  } = props;

  const { classes, cx } = useStyles({
    includeHeight,
    backgroundColor,
    includePadding,
  });
  const style: any = {};
  if (includeMaxWidth !== undefined) {
    if (typeof includeMaxWidth === 'boolean') {
      style.maxWidth = 344;
    } else {
      style.maxWidth = includeMaxWidth;
    }
  }
  if (includeHeight !== undefined) style.height = includeHeight;

  return (
    <Paper square className={cx(classes.paper, className)}>
      <Box
        style={style}
        className={cx(classes.paperChild, paperClasses?.paperChild)}
      >
        {children}
      </Box>
      {includeDismissIcon && (
        <Button
          variant="icon"
          aria-label="close"
          className={classes.modalCloseButton}
          onClick={onClose}
        >
          <Icon type="ex" color="disabled" />
        </Button>
      )}
    </Paper>
  );
};

export default ModalBodyPaper;
